<template>
  <div :class="bannerClasses" class="abe-Wrapper">
    <template v-if="type === APP_BANNER_EXTENDED_TYPES.VERTICAL">
      <div class="abe-Wrapper_Header">
        <div class="abe-Header_Overline">
          <slot name="overline"> </slot>
          <div class="abe-Header_Actions">
            <slot name="header-actions"></slot>
            <AppButtonNext
              v-if="closeable"
              :icon-density="BUTTON_ICON_DENSITIES.FIT"
              :size="BUTTON_SIZES_NEXT.XS"
              icon-after="close-sm"
              @click="onClose"
            />
          </div>
        </div>
        <div v-if="$slots.title || $slots.description" class="abe-Header_TitleContainer">
          <div v-if="$slots.title" class="abe-Title">
            <slot name="title" />
          </div>
          <div v-if="$slots.description" class="abe-Description">
            <slot name="description" />
          </div>
        </div>
      </div>
      <slot name="content"></slot>
      <slot name="actions"> </slot>
    </template>
    <template v-else-if="type === APP_BANNER_EXTENDED_TYPES.HORIZONTAL">
      <div class="abe-Wrapper_LeftContent">
        <slot name="left-content">
          <div v-if="$slots.title || $slots.description" class="abe-Header_TitleContainer">
            <div v-if="$slots.title" class="abe-Title">
              <slot name="title" />
            </div>
            <div v-if="$slots.description" class="abe-Description">
              <slot name="description" />
            </div>
          </div>
        </slot>
        <slot name="left-actions"> </slot>
      </div>
      <div class="abe-Wrapper_RightContent">
        <slot name="right-content"> </slot>
        <AppButtonNext
          v-if="closeable"
          :icon-density="BUTTON_ICON_DENSITIES.FIT"
          :size="BUTTON_SIZES_NEXT.XS"
          data-auto-testid="close-banner-button"
          data-testid="close-banner-button"
          icon-after="close-common"
          @click="onClose"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import { APP_BANNER_EXTENDED_TYPES } from '@/utils/components-configurations/app-banner-extended'
import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT
} from '@/utils/components-configurations/app-button'
import { includesValue } from '@/utils/prop-validators'

import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'

defineOptions({
  name: 'AppBannerExtended'
})

const props = defineProps({
  type: {
    type: String,
    default: APP_BANNER_EXTENDED_TYPES.VERTICAL,
    validator: v => includesValue(v, APP_BANNER_EXTENDED_TYPES)
  },

  closeable: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits({
  close: null
})

const bannerClasses = computed(() => {
  return {
    [`abe-Wrapper-${props.type}`]: props.type
  }
})

const onClose = () => {
  emit('close')
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/variables';

.abe-Wrapper {
  padding: 18px 14px 14px var(--ob-abe-pl, 18px);
  border-radius: 6px;
  border-right: 4px solid var(--primary-color-next-800);
  border-bottom: 4px solid var(--primary-color-next-800);
  background: var(--primary-color-next-600);
  color: $white;
}

.abe-Wrapper-vertical {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
}

.abe-Wrapper-horizontal {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
}

.abe-Wrapper_Header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.abe-Header_Overline {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.abe-Header_TitleContainer {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.abe-Title {
  font-size: $fs-14;
  font-weight: fw('bold');
  line-height: 20px;
}

.abe-Description {
  font-size: 12px;
  font-style: normal;
  font-weight: fw('regular');
  line-height: 18px; /* 150% */
}

.abe-Wrapper_LeftContent {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  justify-content: space-between;
  flex-grow: 1;
}

.abe-Wrapper_RightContent {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.abe-Header_Actions {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
}
</style>
